/**
 * Plan category bucket expansion for SIP plan lists.
 * Public API: useSipPlanCategoryExpansionState, useSipPlanCategoryList, useSipPlanCategoryBucket.
 *
 * Default expand (when scope has no prior expansion): only the first stop SIP bucket in list
 * order, else the first start SIP. All other buckets stay collapsed. For a non-ALL action
 * filter, expand only the first visible bucket of that filter type.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SipCategoryBucketKey, SipActionFilter, SipPlanMode } from '../api/types'
import {
  buildSipCategoryBucketCompositeKey,
  getVisibleSipCategoryBucketKeys,
  type CategoryWithSipBuckets
} from '../../utils/sip/planListUtils'
import { useSipPlansContext } from './sipPlansContext'

type SipPlanCategoryEntry<T extends CategoryWithSipBuckets> = {
  category: T
  categoryIndex: number
}

type ExpansionScopeState = Record<string, Record<string, boolean>>

type ExpansionSyncTargets = {
  defaultExpandedKeys: Record<string, boolean>
  orderedCompositeKeys: string[]
}

type ExpansionScopeUpdate = {
  nextExpandedKeys: Record<string, boolean>
  scrollTarget: string | null
  shouldUpdateKeys: boolean
}

export type SipPlanCategoryExpansionState = {
  expandedBucketKeysByScope: ExpansionScopeState
  applyExpansionSync: (
    planMode: SipPlanMode,
    actionFilter: SipActionFilter,
    syncTargets: ExpansionSyncTargets
  ) => void
  toggleBucketExpanded: (
    planMode: SipPlanMode,
    actionFilter: SipActionFilter,
    bucketCompositeKey: string
  ) => void
  pendingBucketScrollKey: string | null
  clearPendingBucketScroll: () => void
}

export const BUCKET_SCROLL_INTO_VIEW_ENABLED = false

const BUCKET_SCROLL_MIN_VISIBLE_RATIO = 0.1
const BUCKET_SCROLL_TARGET_VIEWPORT_RATIO = 0.4

export function useSipPlanCategoryExpansionState(): SipPlanCategoryExpansionState {
  const [expandedBucketKeysByScope, setExpandedBucketKeysByScope] = useState<ExpansionScopeState>({})
  const [pendingBucketScrollKey, setPendingBucketScrollKey] = useState<string | null>(null)

  const expandedBucketKeysByScopeRef = useRef(expandedBucketKeysByScope)
  expandedBucketKeysByScopeRef.current = expandedBucketKeysByScope

  const clearPendingBucketScroll = useCallback(() => {
    setPendingBucketScrollKey(null)
  }, [])

  const applyExpansionSync = useCallback(
    (scopePlanMode: SipPlanMode, scopeActionFilter: SipActionFilter, syncTargets: ExpansionSyncTargets) => {
      const expandedScopeKey = buildExpansionScopeKey(scopePlanMode, scopeActionFilter)
      const previousForScope = expandedBucketKeysByScopeRef.current[expandedScopeKey] ?? {}
      const { nextExpandedKeys, scrollTarget, shouldUpdateKeys } = resolveScopeExpansionUpdate(
        previousForScope,
        syncTargets
      )

      setPendingBucketScrollKey(scrollTarget)

      if (!shouldUpdateKeys) {
        return
      }

      setExpandedBucketKeysByScope((previousByScope) => ({
        ...previousByScope,
        [expandedScopeKey]: nextExpandedKeys
      }))
    },
    []
  )

  const toggleBucketExpanded = useCallback(
    (scopePlanMode: SipPlanMode, scopeActionFilter: SipActionFilter, bucketCompositeKey: string) => {
      const expandedScopeKey = buildExpansionScopeKey(scopePlanMode, scopeActionFilter)

      setExpandedBucketKeysByScope((previousByScope) => {
        const previousForScope = previousByScope[expandedScopeKey] ?? {}

        return {
          ...previousByScope,
          [expandedScopeKey]: {
            ...previousForScope,
            [bucketCompositeKey]: !previousForScope[bucketCompositeKey]
          }
        }
      })
    },
    []
  )

  return useMemo(
    (): SipPlanCategoryExpansionState => ({
      expandedBucketKeysByScope,
      applyExpansionSync,
      toggleBucketExpanded,
      pendingBucketScrollKey,
      clearPendingBucketScroll
    }),
    [
      expandedBucketKeysByScope,
      applyExpansionSync,
      toggleBucketExpanded,
      pendingBucketScrollKey,
      clearPendingBucketScroll
    ]
  )
}

export function useSipPlanCategoryList<T extends CategoryWithSipBuckets>(
  categories: T[] | null | undefined,
  actionFilter: SipActionFilter,
  planMode: SipPlanMode
): SipPlanCategoryEntry<T>[] {
  const {
    expandedBucketKeysByScope,
    applyExpansionSync,
    pendingBucketScrollKey,
    clearPendingBucketScroll
  } = useSipPlansContext()

  const expandedScopeKey = buildExpansionScopeKey(planMode, actionFilter)
  const expandedBucketKeys = expandedBucketKeysByScope[expandedScopeKey] ?? {}

  const categoryEntries = useMemo(
    () => buildVisibleCategoryEntries(categories, actionFilter),
    [categories, actionFilter]
  )

  useEffect(() => {
    applyExpansionSync(planMode, actionFilter, buildExpansionSyncTargets(categoryEntries, actionFilter))
  }, [categoryEntries, actionFilter, planMode, applyExpansionSync])

  usePendingBucketScroll(pendingBucketScrollKey, expandedBucketKeys, clearPendingBucketScroll)

  return categoryEntries
}

export type SipPlanCategoryBucketParams = {
  planMode: SipPlanMode
  actionFilter: SipActionFilter
  categoryId: string
  bucketKey: SipCategoryBucketKey
}

export function useSipPlanCategoryBucket({
  planMode,
  actionFilter,
  categoryId,
  bucketKey
}: SipPlanCategoryBucketParams): {
  bucketCompositeKey: string
  isExpanded: boolean
  toggle: () => void
} {
  const { expandedBucketKeysByScope, toggleBucketExpanded } = useSipPlansContext()
  const bucketCompositeKey = buildSipCategoryBucketCompositeKey({ categoryId, bucketKey })
  const expandedScopeKey = buildExpansionScopeKey(planMode, actionFilter)
  const expandedBucketKeys = expandedBucketKeysByScope[expandedScopeKey] ?? {}

  return {
    bucketCompositeKey,
    isExpanded: expandedBucketKeys[bucketCompositeKey] ?? false,
    toggle: () => toggleBucketExpanded(planMode, actionFilter, bucketCompositeKey)
  }
}

function usePendingBucketScroll(
  pendingBucketScrollKey: string | null,
  expandedBucketKeys: Record<string, boolean>,
  clearPendingBucketScroll: () => void
): void {
  useEffect(() => {
    if (!BUCKET_SCROLL_INTO_VIEW_ENABLED) {
      if (pendingBucketScrollKey) {
        clearPendingBucketScroll()
      }
      return
    }

    if (!pendingBucketScrollKey || !expandedBucketKeys[pendingBucketScrollKey]) {
      return
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToExpandedBucket(pendingBucketScrollKey)
        clearPendingBucketScroll()
      })
    })
  }, [pendingBucketScrollKey, expandedBucketKeys, clearPendingBucketScroll])
}

function buildExpansionScopeKey(planMode: SipPlanMode, actionFilter: SipActionFilter): string {
  return `${planMode}:${actionFilter}`
}

export { buildExpansionScopeKey }

function buildVisibleCategoryEntries<T extends CategoryWithSipBuckets>(
  categories: T[] | null | undefined,
  actionFilter: SipActionFilter
): SipPlanCategoryEntry<T>[] {
  if (!Array.isArray(categories) || categories.length <= 0) {
    return []
  }

  return categories
    .map((category, categoryIndex) => {
      if (category?.hasCategoryError) {
        if (actionFilter !== SipActionFilter.ALL) {
          return null
        }
        return { category, categoryIndex }
      }
      if (getVisibleSipCategoryBucketKeys(category, actionFilter).length <= 0) {
        return null
      }
      return { category, categoryIndex }
    })
    .filter((entry): entry is SipPlanCategoryEntry<T> => entry != null)
}

function buildExpansionSyncTargets<T extends CategoryWithSipBuckets>(
  categoryEntries: SipPlanCategoryEntry<T>[],
  actionFilter: SipActionFilter
): ExpansionSyncTargets {
  const orderedCompositeKeys: string[] = []
  const defaultExpandedKeys: Record<string, boolean> = {}
  let hasDefaultExpandedBucket = false

  categoryEntries.forEach(({ category, categoryIndex }) => {
    const categoryId = category?.categoryId ?? `category-${categoryIndex}`
    const visibleBucketKeys = getVisibleSipCategoryBucketKeys(category, actionFilter)

    visibleBucketKeys.forEach((bucketKey) => {
      orderedCompositeKeys.push(buildSipCategoryBucketCompositeKey({ categoryId, bucketKey }))
    })

    if (hasDefaultExpandedBucket) {
      return
    }

    const defaultBucketKey =
      actionFilter === SipActionFilter.ALL
        ? resolveDefaultAllFilterBucketKey(visibleBucketKeys)
        : visibleBucketKeys[0]

    if (!defaultBucketKey) {
      return
    }

    defaultExpandedKeys[
      buildSipCategoryBucketCompositeKey({ categoryId, bucketKey: defaultBucketKey })
    ] = true
    hasDefaultExpandedBucket = true
  })

  return { orderedCompositeKeys, defaultExpandedKeys }
}

/** Prefer the first stop bucket, else first start. Other bucket types never auto-expand on ALL. */
function resolveDefaultAllFilterBucketKey(
  visibleBucketKeys: SipCategoryBucketKey[]
): SipCategoryBucketKey | undefined {
  return (
    visibleBucketKeys.find((bucketKey) => bucketKey === SipCategoryBucketKey.stopRecommendations) ??
    visibleBucketKeys.find((bucketKey) => bucketKey === SipCategoryBucketKey.startRecommendations)
  )
}

function resolveScopeExpansionUpdate(
  previousExpandedKeys: Record<string, boolean>,
  { defaultExpandedKeys, orderedCompositeKeys }: ExpansionSyncTargets
): ExpansionScopeUpdate {
  const hasExistingExpansion = Object.values(previousExpandedKeys).some(Boolean)
  const nextExpandedKeys = hasExistingExpansion ? previousExpandedKeys : defaultExpandedKeys
  const scrollTarget = BUCKET_SCROLL_INTO_VIEW_ENABLED
    ? (orderedCompositeKeys.find((compositeKey) => nextExpandedKeys[compositeKey]) ?? null)
    : null

  return {
    nextExpandedKeys,
    scrollTarget,
    shouldUpdateKeys: !areExpandedBucketKeysEqual(previousExpandedKeys, nextExpandedKeys)
  }
}

function areExpandedBucketKeysEqual(previousKeys: Record<string, boolean>, nextKeys: Record<string, boolean>): boolean {
  const previousKeyNames = Object.keys(previousKeys)
  const nextKeyNames = Object.keys(nextKeys)

  if (previousKeyNames.length !== nextKeyNames.length) {
    return false
  }

  return previousKeyNames.every((keyName) => previousKeys[keyName] === nextKeys[keyName])
}

function scrollToExpandedBucket(bucketCompositeKey: string): void {
  if (!BUCKET_SCROLL_INTO_VIEW_ENABLED) {
    return
  }

  const bucketElement = document.querySelector(`[data-plan-category-bucket-key="${bucketCompositeKey}"]`)

  if (!(bucketElement instanceof HTMLElement)) {
    return
  }

  const scrollContainer = findScrollContainer(bucketElement)
  if (!scrollContainer) {
    return
  }

  if (getBucketVisibleRatioInContainer(bucketElement, scrollContainer) >= BUCKET_SCROLL_MIN_VISIBLE_RATIO) {
    return
  }

  const targetScrollTop = getBucketScrollTopForViewportRatio(
    bucketElement,
    scrollContainer,
    BUCKET_SCROLL_TARGET_VIEWPORT_RATIO
  )

  scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'auto' })
}

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement

  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return parent
    }
    parent = parent.parentElement
  }

  return null
}

function getBucketVisibleRatioInContainer(bucketElement: HTMLElement, scrollContainer: HTMLElement): number {
  const bucketRect = bucketElement.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const visibleTop = Math.max(bucketRect.top, containerRect.top)
  const visibleBottom = Math.min(bucketRect.bottom, containerRect.bottom)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const bucketHeight = bucketRect.height

  if (bucketHeight <= 0) {
    return 0
  }

  return visibleHeight / bucketHeight
}

function getBucketScrollTopForViewportRatio(
  bucketElement: HTMLElement,
  scrollContainer: HTMLElement,
  viewportRatio: number
): number {
  const bucketRect = bucketElement.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const bucketTopInViewport = bucketRect.top - containerRect.top
  const targetTopInViewport = scrollContainer.clientHeight * viewportRatio
  const unclampedScrollTop = scrollContainer.scrollTop + bucketTopInViewport - targetTopInViewport
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight

  return Math.max(0, Math.min(unclampedScrollTop, maxScrollTop))
}
